import * as React from 'react';
import Box from '@mui/material/Box';
import { Button, Paper, TextField, Typography, MenuItem } from '@mui/material';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import moment from 'moment-timezone';
import CostChart from './components/CostChart';
import CoGenRealizedSavings from './components/CoGenRealizedSavings';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';


import { useTranslation } from "react-i18next";


import DateRangeSelector from '../../components/DateRangeSelector';
import AlertSnackbar from '../../components/AlertSnackbar';
import SenpilicRealTimeDataVisualizer from './components/SenpilicRealTimeDataVisualizer';
import RecentEventList from '../../components/RecentEventList';
import CoGenSavingsWithBaseline from '../../components/CoGenSavingsWithBaseline';
import CoGenCostBreakdown from './components/CoGenCostBreakdown';
import CircularProgress from '@mui/material/CircularProgress';

import { ApiContext } from "../../App";


export default function SenpilicDashboard(props) {

    const { facility, facilityInfo } = props;

    const { t } = useTranslation();

    const localTimezone = props.clientInfo.timezone;
    const currencySymbol = props.clientInfo.currency_symbol;

    var oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const [fromDate, setFromDate] = React.useState(oneYearAgo);
    const [toDate, setToDate] = React.useState(new Date());
    const [coGenCosts, setCoGenCosts] = React.useState({});
    const [coGenCostsPerPeriod, setCoGenCostsPerPeriod] = React.useState({});
    const [aggType, setAggType] = React.useState("d");
    const [coGenCostByGenerator, setCoGenCostByGenerator] = React.useState({});
    const [coGenEvents, setCoGenEvents] = React.useState([]);
    const [timeRetrievedRealTime, setTimeRetrievedRealTime] = React.useState(new Date());
    const [realTimeData, setRealTimeData] = React.useState({});
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [alertSnackbarOpen, setAlertSnackbarOpen] = React.useState(false);
    const [alertSnackbarText, setAlertSnackbarText] = React.useState("");
    const [realizedSavings, setRealizedSavings] = React.useState({});
    const [savingsType, setSavingsType] = React.useState("projected");

    const {cogenPlanApi, cogenEventApi, planApi, alarmsApi} = React.useContext(ApiContext)

    React.useEffect(() => {
        let interval = setInterval(() => {
            setTimeRetrievedRealTime(new Date());
        }, 1200000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const handleAPIError = () => {
        setAlertMessage(t("Problem fetching data."));
        setAlertOpen(true);
    }

    React.useEffect(() => {
        cogenPlanApi.getCoGenReport(
            facility,
            fromDate,
            toDate,
            data => {
                setCoGenCostsPerPeriod(data);
            },
            () => {
                setCoGenCostsPerPeriod({});
                handleAPIError();
            },
            aggType,
            false
        )
    },
        [fromDate, toDate, aggType, facility]
    )

    React.useEffect(() => {

        cogenPlanApi.getCoGenReport(
            facility,
            fromDate,
            toDate,
            data => {
                setCoGenCosts(data);
            },
            () => {
                setCoGenCosts({});
                handleAPIError();
            }
        )

        cogenPlanApi.getCoGenReportPerGenerator(
            facility,
            fromDate,
            toDate,
            data => {
                setCoGenCostByGenerator(data);
            },
            () => {
                setCoGenCostByGenerator({});
                handleAPIError();
            }
        )

        cogenPlanApi.getRealizedSavingsReport(
            facility,
            fromDate,
            toDate,
            data => {
                setRealizedSavings(data);
                console.log(data)
            },
            () => {
                setRealizedSavings({});
                handleAPIError();
            },
            null
        )

    },
        [fromDate, toDate, facility]
    )

    React.useEffect(() => {
        cogenEventApi.getCoGenEvents(
            facility,
            new Date(),
            data => {

                var filteredData = data.filter((item) => moment.utc(item.timestamp) <= new Date())

                var sortedData = filteredData.sort((a, b) => {
                    if (a.timestamp < b.timestamp) return 1;
                    else return -1;
                })

                var enhancedData = sortedData.map(
                    (item) => ({
                        ...item,
                        identifier: item.generator_identifier,
                        name: item.generator_name,
                        system: t("Co-generation"),
                        location: item.generator_location,
                        value: item.value + " MW",
                        status: "SCHEDULED"
                    })
                )

                setCoGenEvents(enhancedData.slice(0, 10));
            },
            () => {
                setCoGenEvents([]);
                handleAPIError();
            },
            true
        )
    },
        [facility]
    )
    const [realTimeDataIsLoading, setRealTimeDataIsLoading] = React.useState(true)
    React.useEffect(() => {
        planApi.getCurrent(
            facility,
            data => {
                setRealTimeData(data);
                setRealTimeDataIsLoading(false)
            },
            () => {
                setRealTimeData({});
                handleAPIError();
            }
        )
    },
        [timeRetrievedRealTime, facility]
    )

    React.useEffect(() => {
        alarmsApi.list(
            facility,
            data => {
                const count = data.filter((item) => !item.acknowledged && !item.archived).length;
                if (count > 0) {
                    setAlertSnackbarText(count + " " + (count > 1 ? t("new alerts") : t("new alert")));
                    setAlertSnackbarOpen(true);
                }
            },
            () => {
                handleAPIError();
            }
        )
    },
        []
    )

    const handleAggTypeChange = (event, newValue) => {
        setAggType(newValue);
    }


    return (
        <Box
            sx={{
                backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                        ? theme.palette.grey[100]
                        : theme.palette.grey[900],
                overflow: "auto"
            }}>
            <AlertSnackbar
                open={alertSnackbarOpen}
                onClose={() => setAlertSnackbarOpen(false)}
                text={alertSnackbarText}
            />
            <Container sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={2} alignItems="stretch" direction="row">
                    <Grid item xs={12} lg={8}>
                        <Stack spacing={2}>
                            <Paper
                                sx={{
                                    p: 2,

                                }}

                            >
                                <DateRangeSelector
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    onFromDateChange={(newDate) => setFromDate(newDate)}
                                    onToDateChange={(newDate) => setToDate(newDate)}
                                />
                            </Paper>
                            <Paper sx={{
                                p: 2,
                                height: "100%"
                            }}
                            >
                                <Stack spacing={2}>
                                    <Box display="flex">
                                        <Typography flexGrow={1} component="h2" variant="h6" color="primary" gutterBottom>
                                            {t("Cost profile")}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={aggType}
                                            exclusive
                                            size="small"
                                            onChange={handleAggTypeChange}
                                        >
                                            <ToggleButton value="d">
                                                {t("D")}
                                            </ToggleButton>
                                            <ToggleButton value="m">
                                                {t("M")}
                                            </ToggleButton>
                                            <ToggleButton value="q">
                                                {t("Q")}
                                            </ToggleButton>
                                            <ToggleButton value="y">
                                                {t("Y")}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Box height={380}>
                                        <CostChart
                                            data={coGenCostsPerPeriod}
                                            currencySymbol={currencySymbol}
                                            aggType={aggType}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{
                            p: 2,
                            height: "100%"
                        }}
                        >
                            <Box display="flex" flexDirection={"column"} height="100%">
                                <Box display="flex" flexDirection={"row"}>
                                <Typography component="h2" variant="h6" color="primary" flexGrow={1}>
                                    {t("Savings")}
                                </Typography> 
                                <TextField 
                                    select value={savingsType} size="small"
                                    onChange={(event)=>setSavingsType(event.target.value)}
                                >
                                    <MenuItem value="projected">
                                        {t("Projected")}
                                    </MenuItem>
                                    <MenuItem value="realized">
                                        {t("Realized")}
                                    </MenuItem>
                                </TextField>
                                </Box>        
                                {
                                    savingsType === "projected" && <CoGenSavingsWithBaseline
                                    allGridCost={coGenCosts.only_grid?.[0].cost ?? 0.0}
                                    allGridConsumption={coGenCosts.only_grid?.[0].consumption ?? 0.0}
                                    allGridCO2={coGenCosts.only_grid?.[0].co2 ?? 0.0}
                                    gridSolarCost={coGenCosts.baseline?.[0].cost ?? 0.0}
                                    gridSolarConsumption={coGenCosts.baseline?.[0].consumption ?? 0.0}
                                    gridSolarCO2={coGenCosts.baseline?.[0].co2 ?? 0.0}
                                    coGenManualCost={coGenCosts.manual?.[0].cost ?? 0.0}
                                    coGenManualConsumption={coGenCosts.manual?.[0].consumption ?? 0.0}
                                    coGenManualCO2={coGenCosts.manual?.[0].co2 ?? 0.0}
                                    optimizedCost={coGenCosts.optimized?.[0].cost ?? 0.0}
                                    optimizedConsumption={coGenCosts.optimized?.[0].consumption ?? 0.0}
                                    optimizedCO2={coGenCosts.optimized?.[0].co2 ?? 0.0}
                                    dateString={""}
                                    currencySymbol={currencySymbol}
                                    title={""}
                                />
                                }    
                                {
                                    savingsType === "realized" && <CoGenRealizedSavings
                                    solarCost={realizedSavings.data?.find((item)=>item.name==="solar_savings_cost").values[0]}
                                    cogenCost={realizedSavings.data?.find((item)=>item.name==="cogen_savings_cost").values[0]}
                                    solarConsumption={realizedSavings.data?.find((item)=>item.name==="solar_savings_consumption").values[0]}
                                    cogenConsumption={realizedSavings.data?.find((item)=>item.name==="cogen_savings_consumption").values[0]}
                                    solarCO2={realizedSavings.data?.find((item)=>item.name==="solar_savings_co2").values[0]}
                                    cogenCO2={realizedSavings.data?.find((item)=>item.name==="cogen_savings_co2").values[0]}  
                                    currencySymbol={currencySymbol}
                                    />
                                }              
                                
                                
                                <Typography align='center'>
                                    {t("OnDateRange", {
                                        from: fromDate.toLocaleDateString(),
                                        to: toDate.toLocaleDateString(), interpolation: { escapeValue: false }
                                    })}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Paper sx={{
                            p: 2,
                            height: "100%"
                        }}
                        >
                            <Stack spacing={2}>
                                <Box display="flex">
                                    <Typography flexGrow={1} component="h2" variant="h6" color="primary" gutterBottom>
                                        {t("Cost breakdown")}
                                    </Typography>
                                </Box>
                                <Box height={350} sx={{overflowY: "auto", overflowX: "clip"}}>
                                    <CoGenCostBreakdown
                                        currencySymbol={currencySymbol}
                                        coGen={coGenCostByGenerator}
                                    />
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{
                            p: 2,
                            height: "100%",
                        }}
                        >
                            {
                                realTimeDataIsLoading ?
                                    <Box sx={{
                                        textAlign: "center",
                                        alignContent: "center",
                                        height: "100%"
                                    }}>
                                        <CircularProgress />
                                    </Box>
                                    :
                                    <Box display="flex" flexDirection={"column"} height="100%">
                                        <Box display="flex">
                                            <Typography flex={1} component="h2" variant="h6" color="primary" gutterBottom>
                                                {t("Real-time data")}
                                            </Typography>
                                            <Typography variant="subtitle1">
                                                {timeRetrievedRealTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </Typography>
                                        </Box>
                                        <Box flex={1} />
                                        <SenpilicRealTimeDataVisualizer
                                            currencySymbol={currencySymbol}
                                            data={realTimeData}
                                        />
                                        <Box flex={1} />
                                    </Box>
                            }
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{
                            p: 2,
                            height: "100%"
                        }}
                        >
                            <Typography flexGrow={1} component="h2" variant="h6" color="primary" gutterBottom>
                                {t("Latest events")}
                            </Typography>
                            <Box height={300}>
                                <RecentEventList events={coGenEvents} localTimezone={localTimezone} />
                            </Box>
                        </Paper>
                    </Grid>

                </Grid>
            </Container>
            <Dialog
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>


    )

};
